<template>
  <v-btn
    :class="{
      disabled: disabled,
      error: error,
      primary: primary,
      secondary: secondary,
      overlaying: overlaying,
      warning: warning,
      grey: grey,
      text: text,
      'no-click': disableClick,
      float: float,
      rejected: rejected,
      success: success,
    }"
    v-bind="attrs"
    v-on="on"
    :color="parseColor"
    depressed
    :rounded="round"
    :outlined="secondary"
    :disabled="disabled"
    :min-width="minWidth"
    :min-height="minHeight"
    :block="block"
    :small="small"
    :width="currentWidth"
    :loading="loading"
    :text="text"
    :fixed="fixed"
    :x-small="xSmall"
    :ripple="!disableClick"
    :to="to"
    :href="href"
    :target="target"
    :type="type"
    @click="$emit('click')"
  >
    <slot></slot>
  </v-btn>
</template>

<script>
import Loader from "Components/Loader";
import checkMobileMixin from "Shared/mixins/checkMobileMixin";
export default {
  name: "Btn",
  components: { Loader },
  mixins: [checkMobileMixin],
  props: {
    overlaying: {
      type: Boolean,
      default: false,
    },
    href: {
      type: String | Object,
      default: undefined,
    },
    target: {
      type: String,
      default: undefined,
    },
    to: {
      type: String | Object,
      default: undefined,
    },
    disableClick: {
      type: Boolean,
      default: false,
    },
    attrs: {
      type: Object,
      default: () => {
        return {};
      },
    },
    on: {
      type: Object,
      default: () => {
        return {};
      },
    },
    color: {
      type: String,
      default: "primary",
    },
    round: {
      type: Boolean,
      default: false,
    },
    fixed: {
      type: Boolean,
      default: false,
    },
    grey: {
      type: Boolean,
      default: false,
    },
    text: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 0,
    },
    width: {
      type: Number,
      default: 0,
    },
    mobileWidth: {
      type: Number,
      default: 0,
    },
    float: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: false,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    xSmall: {
      type: Boolean,
      default: false,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    rejected: {
      type: Boolean,
      default: false,
    },
    success: {
      type: Boolean,
      default: false,
    },
    type: {
      type: "button" | "submit",
      default: "button",
    },
  },
  computed: {
    currentWidth() {
      if (this.mobileWidth && this.mobile) return this.mobileWidth;
      if (!this.width) return "auto";
      return this.width;
    },
    minWidth() {
      if (this.mobileWidth && this.mobile) return this.mobileWidth;
      if (this.width) return this.width;
      if (this.icon) return;
      if (this.xSmall) return;
      return this.mobile || this.small ? 100 : 150;
    },
    minHeight() {
      if (this.height) return this.height;
      if (this.xSmall) return;
      return 40;
    },
    parseColor() {
      if (this.disabled) return "disabled";
      if (this.warning) return "warning";
      return this.color;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~PublicStyles/_variables.scss";
.v-btn {
  text-transform: none !important;
  padding: 10px 20px;
  transition: background 0.2s linear;

  &.primary {
    &:hover {
      border: 1px $orange_hover solid !important;
      background: $white !important;
      color: $orange_hover !important;
    }

    &.disable-click {
      pointer-events: none;
    }

    &.rejected {
      background: $barney !important;
      color: $white !important;

      &:hover {
        border: 1px $barney solid !important;
        background: $white !important;
        color: $barney !important;
      }
    }

    &.error {
      background: $error !important;
      color: $white !important;

      &:hover {
        border: 1px $error solid !important;
        background: $white !important;
        color: $error !important;
      }
    }

    &.warning {
      background: $warning !important;

      &:hover {
        border: 1px $warning solid !important;
        background: $white !important;
        color: $warning !important;
      }
    }

    &.disabled {
      border: 1px $disabled solid !important;
      color: rgba(0, 0, 0, 0.26) !important;
      background: $disabled !important;
    }

    &.success {
      background: $success !important;

      &:hover {
        border: 1px $success solid !important;
        background: $white !important;
        color: $success !important;
      }
    }

    &.float {
      border-radius: 8px 0 0 8px !important;
      @media ($tablet) {
        border-radius: 100px !important;
      }
    }
  }

  &.secondary {
    border: 1px $orange solid !important;
    background: $white;
    color: $orange;

    &.disable-click {
      pointer-events: none;
    }

    &:hover {
      border: 1px $orange_hover solid !important;
      color: $white !important;
      background: $orange_hover !important;
    }

    &.error {
      border: 1px $error solid !important;
      color: $error !important;
      background: $white !important;

      &:hover {
        color: $white !important;
        background: $error !important;
      }
    }

    &.rejected {
      border: 1px $barney solid !important;
      color: $barney !important;
      background: $white !important;

      &:hover {
        color: $white !important;
        background: $barney !important;
      }
    }

    &.warning {
      border: 1px $warning solid !important;
      color: $warning !important;
      background: $white !important;

      &:hover {
        color: $white !important;
        background: $warning !important;
      }
    }

    &.disabled {
      border: 1px $disabled solid !important;
      color: rgba(0, 0, 0, 0.26) !important;
      background: $disabled !important;
    }

    &.success {
      border: 1px $success solid !important;
      color: $success !important;
      background: $white !important;

      &:hover {
        color: $white !important;
        background: $success !important;
      }
    }
  }

  .overlaying {
    border: 1px $orange solid !important;
    color: $orange !important;
    background: $white !important;
  }

  &.text {
    &.error {
      background: $white !important;
      color: $error !important;
    }
  }

  &.grey {
    color: #acafb5 !important;
    background: $white !important;
  }
  //&.v-btn--outlined {
  //  &:hover {
  //    border: 1px $white solid !important;
  //    color: $white !important;
  //    background: $orange_hover !important;
  //  }
  //}
}
</style>
